import React, { useState, useEffect } from 'react';

// Components
import FormattedMessageV2, { ALLOWED_HTML_TAGS } from '../../i18n/FormattedMessageV2';
import { CloseButton, SecondaryButton, PrimaryButton } from '@/views/components/buttons';
import { ModelsPortfoliosList } from '@/views/components/modals//modal-content';
import Modal from '@/views/components/modals/AccessibleModal';
import { Typography } from '@capintel/component-lib/typography';

// Enums
import { modelType } from '@/types/data';
import { MODEL_PORTFOLIO_TYPES } from '@/enums/Models';
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '@/enums/Portfolio';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import { connect } from 'react-redux';
import { SortingRule } from 'react-table';
import { useDebouncedCallback } from 'use-debounce';

// Redux
import DisclaimerFooter from '@/views/components/disclaimers/DisclaimerFooter';
import LegacyLoadModelsModal from '@/views/components/modals/comparison-modals/LegacyLoadModelsModal';
import { ADD_PORTFOLIOS_TO_COMPARISON } from '@/redux/modules/comparison';
import { ControlItem, Tabs } from '@/views/components/navigation';
import { getModelsPortfolios } from '@/redux/modules/portfolios';
import { ReduxState } from '@/redux/modules/types';
import { SearchInput } from '@/views/components/inputs';
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';

// Types
import EnterpriseDetails from '@/types/enterprise-detail-info';
import PaginatedPortfolios from '@/types/paginated-portfolios';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import UserPreferences from '@/types/user-preferences';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { isEnterpriseModel } from '@/utils/portfolio/utils';
import { PERMISSIONS, userHasPermission, userHasPermissions } from '@/utils/permissions';

type StateProps = {
  isComparing?: boolean;
  portfolios: Portfolio[];
  myPortfolios: Portfolio[];
  enterprisePortfolios: Portfolio[];
  enterprise: EnterpriseDetails;
  nextModelsToken?: string;
  nextMyModelsToken?: string;
  nextEnterpriseModelsToken?: string;
  userPreferences?: UserPreferences;
};

type DispatchProps = {
  getModelsPortfolios: (type?: modelType, query?: string, sort?: SortingRule[], nextToken?: string) => Promise<Portfolio[]>;
};

type Props = StateProps &
  DispatchProps & {
    user: User;
    show: boolean;
    onCancel: () => void;
    addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  };

const LoadModelsModalV2 = (props: Props) => {
  const {
    user,
    portfolios,
    myPortfolios,
    enterprisePortfolios,
    show,
    nextModelsToken,
    nextMyModelsToken,
    nextEnterpriseModelsToken,
    enterprise,
    isComparing,
    getModelsPortfolios,
  } = props;

  const options = [
    {
      value: MODEL_PORTFOLIO_TYPES.ALL_MODELS,
      label: formatMessage('Modal.ModelsPortfoliosList.AllModels'),
      hidden:
        !userHasPermission(user, PERMISSIONS.ALL_MODELS_LIST) ||
        !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST) ||
        !userHasPermission(user, PERMISSIONS.USER_MODELS),
    },
    {
      value: MODEL_PORTFOLIO_TYPES.MY_MODELS,
      label: formatMessage('Modal.ModelsPortfoliosList.MyModels'),
      hidden: !userHasPermission(user, PERMISSIONS.USER_MODELS),
    },
    {
      value: MODEL_PORTFOLIO_TYPES.ENTERPRISE_MODELS,
      label: enterprise && formatMessage('Modal.ModelsPortfoliosList.EnterpriseTitle'),
      hidden: !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST),
    },
  ].filter(i => !i.hidden);

  const [selectedPortfolios, setSelectedPortfolios] = useState<Portfolio[]>([]);
  const [sorting, setSorting] = useState<SortingRule[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<MODEL_PORTFOLIO_TYPES>(options.length > 0 ? options[0].value : MODEL_PORTFOLIO_TYPES.ALL_MODELS);

  const onSearch = (newQuery: string) => {
    setLoading(true);
    setQuery(newQuery);
  };

  const queryPortfolios = useDebouncedCallback((searchQuery: string, sortingRules: SortingRule[]) => {
    const promises: Promise<Portfolio[] | PaginatedPortfolios>[] = [];

    if (userHasPermissions(user, [PERMISSIONS.ALL_MODELS_LIST, PERMISSIONS.USER_MODELS, PERMISSIONS.ENTERPRISE_MODELS_LIST])) {
      promises.push(getModelsPortfolios(undefined, searchQuery, sortingRules));
    }

    if (userHasPermission(user, PERMISSIONS.USER_MODELS)) {
      // My Models
      promises.push(getModelsPortfolios(modelType.User, searchQuery, sortingRules));
    }

    if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST) && enterprise) {
      // Enterprise Models
      promises.push(getModelsPortfolios(modelType.Enterprise, searchQuery, sortingRules));
    }

    Promise.all(promises).then(() => {
      setLoading(false);
    });
  }, 300);

  useEffect(() => {
    queryPortfolios(query, sorting);
  }, [sorting, query, queryPortfolios]);

  if (!user) {
    return null;
  }

  const hasMoreModels = !!nextModelsToken;
  const hasMoreMyModels = !!nextMyModelsToken;
  const hasMoreEnterpriseModels = !!nextEnterpriseModelsToken;

  const handleSelectionChange = (selectedRows: Portfolio[]) => {
    setSelectedPortfolios(selectedRows);
  };

  const onClose = () => {
    props.onCancel();
    setSelectedPortfolios([]);
  };

  const onLoadSelectedPortfolios = () => {
    selectedPortfolios.forEach(selectedPortfolio => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_MODEL, { isEnterpriseModel: isEnterpriseModel(selectedPortfolio) });
    });

    props.addPortfoliosToComparison(selectedPortfolios).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
    });
  };

  const loadMoreModelPortfolios = () => {
    getModelsPortfolios(undefined, query, sorting, nextModelsToken);
  };

  const loadMoreMyModelsPortfolios = () => {
    getModelsPortfolios(modelType.User, query, sorting, nextMyModelsToken);
  };

  const loadMoreEnterprisePortfolios = () => {
    getModelsPortfolios(modelType.Enterprise, query, sorting, nextEnterpriseModelsToken);
  };

  const getDisplayedColumns = () => {
    const { user } = props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermission(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  const onSort = (sort: SortingRule[]) => {
    setLoading(true);
    setSorting(sort);
  };

  const displayedColumns = getDisplayedColumns();
  const displayedMetrics = [
    PORTFOLIO_METRICS_FIELDS.MER,
    PORTFOLIO_METRICS_FIELDS.YEAR_ONE,
    PORTFOLIO_METRICS_FIELDS.YEAR_THREE,
    PORTFOLIO_METRICS_FIELDS.SIZE,
  ];

  const onOptionChange = (option: MODEL_PORTFOLIO_TYPES) => {
    setSelectedOption(option);
    setSelectedPortfolios([]);
  };

  const renderTable = (): JSX.Element => (
    <ModelsPortfoliosList
      displayedColumns={displayedColumns}
      displayedMetrics={displayedMetrics}
      displayEnterpriseLabels
      emptyStateHeader={formatMessage('EmptyState.NoModelPortfolios.Title')}
      enterprise={enterprise}
      enterprisePortfolios={enterprisePortfolios}
      hasMoreEnterprisePortfolios={hasMoreEnterpriseModels}
      hasMoreMyPortfolios={hasMoreMyModels}
      hasMorePortfolios={hasMoreModels}
      hideStats={false}
      loading={loading}
      loadMoreEnterprisePortfolios={loadMoreEnterprisePortfolios}
      loadMoreMyPortfolios={loadMoreMyModelsPortfolios}
      loadMorePortfolios={loadMoreModelPortfolios}
      myPortfolios={myPortfolios}
      onRowSelectionChange={handleSelectionChange}
      onSearchPortfolios={onSearch}
      onSort={onSort}
      portfolios={portfolios}
      query={query}
      resetSelectedPortfolios={() => setSelectedPortfolios([])}
      selectedOption={selectedOption}
      selectedTitle="PortfoliosTable.SelectedTitle.Models"
      user={user}
    />
  );

  // MODAL HEADER
  const renderHeader = (): JSX.Element => (
    <Modal.Header>
      {/* Provisional fix. Pending migration to library modal. */}
      <div className="modal-title">
        <Typography as={ALLOWED_HTML_TAGS.H2} color="primary" variant="headerXs">
          <FormattedMessageV2 toLocalize="ModelPortfoliosModal.Title" />
        </Typography>
      </div>
      <CloseButton onClick={onClose} />
    </Modal.Header>
  );

  // MODAL BODY
  const renderSearchInput = (): JSX.Element => (
    <div className="modal-search-input">
      <SearchInput
        className="search-input-v2"
        data-private
        data-dd-privacy="mask"
        loading={loading}
        onChange={onSearch}
        placeholder={formatMessage('Modal.ModelsPortfoliosList.SearchPortfolios')}
        value={query}
      />
    </div>
  );

  const renderTabs = (): JSX.Element => (
    <div className="tabs-container">
      <Tabs className="tabs-v2" onChange={option => onOptionChange(option)} selected={selectedOption}>
        {options.map(option => (
          <ControlItem key={option.value} value={option.value}>
            <span data-private data-dd-privacy="mask">
              {option.label}
            </span>
          </ControlItem>
        ))}
      </Tabs>
    </div>
  );

  const renderBody = (): JSX.Element => (
    <Modal.Body>
      {renderSearchInput()}
      {renderTabs()}
      {renderTable()}
      {!loading && <DisclaimerFooter disclaimers={[formatMessage('Disclaimers.NET_PERFORMANCE')]} />}
    </Modal.Body>
  );

  // MODAL FOOTER
  const renderFooter = (): JSX.Element => (
    <Modal.Footer>
      <SecondaryButton className="secondary-v2" onClick={onClose} disabled={isComparing}>
        <FormattedMessage id="Common.Close" />
      </SecondaryButton>

      <PrimaryButton loading={isComparing} disabled={!selectedPortfolios.length} className="primary-v2" onClick={onLoadSelectedPortfolios}>
        <FormattedMessage id={`ModelPortfoliosModal.${isComparing ? 'ComparingSelected' : 'CompareSelection'}`} values={{ count: selectedPortfolios.length }} />
      </PrimaryButton>
    </Modal.Footer>
  );

  return (
    <Modal backdropClassName="modal-v2-backdrop" className="modal-v2 load-models-modal-v2" show={show} onHide={onClose}>
      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </Modal>
  );
};

const LoadModelsModal = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <LoadModelsModalV2 {...props} />;
  }
  return <LegacyLoadModelsModal {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  isComparing: selectIsLoadingEvery(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]),
  portfolios: state.portfolios.models,
  myPortfolios: state.portfolios.myModels,
  enterprisePortfolios: state.portfolios.enterpriseModels,
  enterprise: state.enterprise.details,
  nextModelsToken: state.portfolios.nextModelsToken,
  nextMyModelsToken: state.portfolios.nextMyModelsToken,
  nextEnterpriseModelsToken: state.portfolios.nextEnterpriseModelsToken,
  userPreferences: state.auth.user?.preferences,
});

export default connect(mapStateToProps, {
  getModelsPortfolios,
})(LoadModelsModal);
